import { locale } from '@fortune-sheet/core';

/**
 * One-time Fortune-Sheet global setup. The library exposes no Workbook prop to
 * extend the toolbar font list — it ships only four fonts (Times New Roman,
 * Arial, Tahoma, Verdana). `locale({ lang })` returns the shared locale object
 * BY REFERENCE (a pure read — it does not mutate global lang state), so we
 * append our fonts to `fontarray` once at first editor mount. Selecting a font
 * stores the string name in `cell.ff`, which the converter and xlsx export
 * already round-trip, so appended fonts persist cleanly.
 */

// Appended (never reordered) — a saved sheet may store a numeric `ff` index into
// the original four-entry fontarray; appending keeps those indices valid.
const EXTRA_FONTS = [
    'Gilroy', // aica brand font
    'Calibri',
    'Cambria',
    'Georgia',
    'Roboto',
    'Segoe UI',
    'Helvetica',
    'Helvetica Neue',
    'Courier New',
    'Consolas',
    'Trebuchet MS',
    'Garamond',
    'Century Gothic',
    'Palatino Linotype',
    'Lucida Sans',
];

type FontLocale = { fontarray?: string[] };
// locale() is typed as taking a full Context and returning a large locale
// object; we only read `ctx.lang` and touch `fontarray`, so narrow both sides.
const readEnLocale = locale as unknown as (ctx: { lang: string }) => FontLocale;

let done = false;

/** Idempotently extend Fortune-Sheet's toolbar font list. Safe to call on every
 *  editor mount — the work runs at most once per page load. */
export const ensureSheetFontList = (): void => {
    if (done) return;
    done = true;
    try {
        const en = readEnLocale({ lang: 'en' });
        const current = Array.isArray(en.fontarray) ? en.fontarray : [];
        const seen = new Set(current.map((f) => f.toLowerCase()));
        en.fontarray = [...current, ...EXTRA_FONTS.filter((f) => !seen.has(f.toLowerCase()))];
    } catch {
        // Locale shape changed in a library upgrade — non-fatal; the dropdown
        // simply keeps its built-in fonts.
    }
};
